import { ExecutionDetails } from '@frontend/types/agentExecution';
import { StepExecution } from '@frontend/types/stepExecution';

export function findStepExecutionsByStepId(
  stepExecutions: StepExecution[],
  targetStepId: string
): StepExecution[] {
  const results: StepExecution[] = [];

  for (const stepExecution of stepExecutions) {
    // Check if current step execution matches the target step_id
    if (stepExecution.step_id === targetStepId) {
      results.push(stepExecution);
    }

    // Recursively search through children (for loop iterations, etc.)
    if (stepExecution.children && stepExecution.children.length > 0) {
      const childResults = findStepExecutionsByStepId(stepExecution.children, targetStepId);
      results.push(...childResults);
    }
  }

  return results;
}

export function getStepExecutionDetailsByStepId(
  executionDetails: ExecutionDetails,
  stepId: string
): StepExecution[] {
  if (!executionDetails.step_executions) {
    return [];
  }

  return findStepExecutionsByStepId(executionDetails.step_executions as StepExecution[], stepId);
}

export function getStepExecutionSummary(executionDetails: ExecutionDetails, stepId: string) {
  const executions = getStepExecutionDetailsByStepId(executionDetails, stepId);

  return {
    stepId,
    totalExecutions: executions.length,
    executions: executions.map(exec => ({
      executionId: exec.id || '',
      status: exec.status,
      startTime: exec.start_time || '',
      endTime: exec.end_time || null,
      hasError: exec.error_message !== null && exec.error_message !== undefined,
      errorMessage: exec.error_message || null,
      parameters: exec.parameters || {},
      result: exec.result || null,
      retryCount: exec.retry_count || 0,
      parentStepExecutionId: exec.parent_step_execution_id || null,
      iterationExecutionId: exec.iteration_execution_id || null,
    })),
  };
}

export function formatExecutionTime(startTime?: string, endTime?: string | null): string {
  if (!startTime) return 'Unknown';

  const start = new Date(startTime);
  const formattedStart = start.toLocaleString();

  if (endTime) {
    const end = new Date(endTime);
    const duration = end.getTime() - start.getTime();
    const seconds = Math.round(duration / 1000);
    return `${formattedStart} (${seconds}s)`;
  }

  return formattedStart;
}

export function getStatusColor(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
      return 'warning';
    case 'pending':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/**
 * Recursively counts artifacts in a step execution and all its children
 * This is especially important for ForLoop nodes that have nested executions
 */
export function countArtifactsRecursively(stepExecution: StepExecution): number {
  let count = 0;

  // Count artifacts from the current execution
  if (stepExecution.execution?.artifacts) {
    count += stepExecution.execution.artifacts.length;
  }

  // Recursively count artifacts from children (for loop iterations, etc.)
  if (stepExecution.children && stepExecution.children.length > 0) {
    for (const child of stepExecution.children) {
      count += countArtifactsRecursively(child);
    }
  }

  return count;
}

/**
 * Gets total artifact count for a specific step across all its executions
 * Includes artifacts from nested/child executions (e.g., ForLoop iterations)
 */
export function getTotalArtifactCountForStep(
  executionDetails: ExecutionDetails,
  stepId: string
): number {
  const executions = getStepExecutionDetailsByStepId(executionDetails, stepId);

  return executions.reduce((total, execution) => {
    return total + countArtifactsRecursively(execution);
  }, 0);
}

/**
 * Recursively aggregates token usage from a step execution and all its children
 */
export function aggregateTokenUsageRecursively(stepExecution: StepExecution): {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  thinking_tokens: number;
  cached_tokens: number;
} {
  const tokenUsage = {
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    thinking_tokens: 0,
    cached_tokens: 0,
  };

  // Add tokens from the current execution
  if (stepExecution.result?.token_usage) {
    tokenUsage.input_tokens += stepExecution.result.token_usage.input_tokens || 0;
    tokenUsage.output_tokens += stepExecution.result.token_usage.output_tokens || 0;
    tokenUsage.total_tokens += stepExecution.result.token_usage.total_tokens || 0;
    tokenUsage.thinking_tokens += stepExecution.result.token_usage.thinking_tokens || 0;
    tokenUsage.cached_tokens += stepExecution.result.token_usage.cached_tokens || 0;
  } else if (stepExecution.token_usage) {
    tokenUsage.input_tokens += stepExecution.token_usage.input_tokens || 0;
    tokenUsage.output_tokens += stepExecution.token_usage.output_tokens || 0;
    tokenUsage.total_tokens += stepExecution.token_usage.total_tokens || 0;
    tokenUsage.thinking_tokens += stepExecution.token_usage.thinking_tokens || 0;
    tokenUsage.cached_tokens += stepExecution.token_usage.cached_tokens || 0;
  }

  // Recursively add tokens from children
  if (stepExecution.children && stepExecution.children.length > 0) {
    for (const child of stepExecution.children) {
      const childTokens = aggregateTokenUsageRecursively(child);
      tokenUsage.input_tokens += childTokens.input_tokens;
      tokenUsage.output_tokens += childTokens.output_tokens;
      tokenUsage.total_tokens += childTokens.total_tokens;
      tokenUsage.thinking_tokens += childTokens.thinking_tokens;
      tokenUsage.cached_tokens += childTokens.cached_tokens;
    }
  }

  return tokenUsage;
}

/**
 * Gets total token usage for all step executions in a workflow
 * Includes tokens from nested/child executions (e.g., ForLoop iterations)
 * Also includes workflow orchestrator memory tokens
 */
export function getTotalTokenUsageForWorkflow(executionDetails: ExecutionDetails): {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  thinking_tokens: number;
  cached_tokens: number;
} {
  const tokenUsage = {
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    thinking_tokens: 0,
    cached_tokens: 0,
  };

  // Add workflow orchestrator memory tokens
  if (executionDetails.result?.token_usage) {
    tokenUsage.input_tokens += executionDetails.result.token_usage.input_tokens || 0;
    tokenUsage.output_tokens += executionDetails.result.token_usage.output_tokens || 0;
    tokenUsage.total_tokens += executionDetails.result.token_usage.total_tokens || 0;
    tokenUsage.thinking_tokens += executionDetails.result.token_usage.thinking_tokens || 0;
    tokenUsage.cached_tokens += executionDetails.result.token_usage.cached_tokens || 0;
  }

  // Add agent execution tokens
  if (executionDetails.step_executions) {
    (executionDetails.step_executions as StepExecution[]).forEach(stepExecution => {
      const stepTokens = aggregateTokenUsageRecursively(stepExecution);
      tokenUsage.input_tokens += stepTokens.input_tokens;
      tokenUsage.output_tokens += stepTokens.output_tokens;
      tokenUsage.total_tokens += stepTokens.total_tokens;
      tokenUsage.thinking_tokens += stepTokens.thinking_tokens;
      tokenUsage.cached_tokens += stepTokens.cached_tokens;
    });
  }

  return tokenUsage;
}

export type ExecutionTokenUsage = {
  executionId: string;
  executionNumber: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  thinking_tokens: number;
  cached_tokens: number;
};

export type TokenUsageByAgent = {
  [agentId: string]: {
    agentName: string;
    agentId: string;
    executionCount: number;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    thinking_tokens: number;
    cached_tokens: number;
    executions: ExecutionTokenUsage[];
  };
};

/**
 * Recursively processes step executions to group token usage by agent
 */
function processStepExecutionForTokens(step: StepExecution, grouped: TokenUsageByAgent): void {
  // Get token usage from this step (not including children)
  const stepTokens = {
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    thinking_tokens: 0,
    cached_tokens: 0,
  };

  // Get direct token usage (not recursive)
  if (step.result?.token_usage) {
    stepTokens.input_tokens = step.result.token_usage.input_tokens || 0;
    stepTokens.output_tokens = step.result.token_usage.output_tokens || 0;
    stepTokens.total_tokens = step.result.token_usage.total_tokens || 0;
    stepTokens.thinking_tokens = step.result.token_usage.thinking_tokens || 0;
    stepTokens.cached_tokens = step.result.token_usage.cached_tokens || 0;
  } else if (step.token_usage) {
    stepTokens.input_tokens = step.token_usage.input_tokens || 0;
    stepTokens.output_tokens = step.token_usage.output_tokens || 0;
    stepTokens.total_tokens = step.token_usage.total_tokens || 0;
    stepTokens.thinking_tokens = step.token_usage.thinking_tokens || 0;
    stepTokens.cached_tokens = step.token_usage.cached_tokens || 0;
  }

  // Only count if there are actual tokens
  if (stepTokens.total_tokens > 0 && step.agent) {
    const agentId = step.agent.agent_id || 'unknown';
    const agentName = step.agent.name || 'Unknown Agent';
    const executionId = step.id || 'unknown';

    if (!grouped[agentId]) {
      grouped[agentId] = {
        agentId,
        agentName,
        executionCount: 0,
        input_tokens: 0,
        output_tokens: 0,
        total_tokens: 0,
        thinking_tokens: 0,
        cached_tokens: 0,
        executions: [],
      };
    }

    // Add execution details
    grouped[agentId].executionCount += 1;
    grouped[agentId].input_tokens += stepTokens.input_tokens;
    grouped[agentId].output_tokens += stepTokens.output_tokens;
    grouped[agentId].total_tokens += stepTokens.total_tokens;
    grouped[agentId].thinking_tokens += stepTokens.thinking_tokens;
    grouped[agentId].cached_tokens += stepTokens.cached_tokens;
    grouped[agentId].executions.push({
      executionId,
      executionNumber: grouped[agentId].executionCount,
      input_tokens: stepTokens.input_tokens,
      output_tokens: stepTokens.output_tokens,
      total_tokens: stepTokens.total_tokens,
      thinking_tokens: stepTokens.thinking_tokens,
      cached_tokens: stepTokens.cached_tokens,
    });
  }

  // Process children to capture their agent information
  if (step.children && step.children.length > 0) {
    step.children.forEach(child => {
      processStepExecutionForTokens(child, grouped);
    });
  }
}

/**
 * Groups token usage by agent across all step executions
 */
export function groupTokenUsageByAgent(
  stepExecutions: StepExecution[] | unknown[]
): TokenUsageByAgent {
  const grouped: TokenUsageByAgent = {};

  if (!Array.isArray(stepExecutions)) {
    return grouped;
  }

  stepExecutions.forEach((stepExecution: StepExecution | unknown) => {
    const step = stepExecution as StepExecution;
    processStepExecutionForTokens(step, grouped);
  });

  return grouped;
}
